import React from 'react';
import { useParams, Link } from 'react-router-dom';
import { ChevronLeft, Mail, MousePointer, Eye, DollarSign, Users, TrendingUp } from 'lucide-react';
import { Card } from '@/components/ui/card';
import { AdminLayout } from '@/components/layout/AdminLayout';
import { mockCampaigns } from '@/lib/mockData';
import { formatCurrency, formatNumber } from '@/lib/utils';

export function AdminCampaignMetrics() {
  const { campaignId } = useParams();
  const campaign = mockCampaigns.find(c => c.id === campaignId) || mockCampaigns[0];

  const openRate = ((campaign.opened / campaign.sent) * 100).toFixed(1);
  const clickRate = ((campaign.clicked / campaign.sent) * 100).toFixed(1);
  const conversionRate = ((campaign.conversions / campaign.clicked) * 100).toFixed(1);

  return (
    <AdminLayout>
      <div className="p-8">
        <Link to="/admin" className="inline-flex items-center text-muted-foreground hover:text-white mb-6">
          <ChevronLeft className="w-5 h-5 mr-1" />
          Back to Dashboard
        </Link>

        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl font-bold text-white mb-2">{campaign.name}</h1>
            <div className="flex items-center gap-3">
              <span className={`px-3 py-1 rounded-full text-xs font-semibold ${
                campaign.status === 'active' 
                  ? 'bg-green-500/20 text-green-500' 
                  : 'bg-muted text-muted-foreground'
              }`}>
                {campaign.status === 'active' ? 'Active' : 'Completed'}
              </span>
              <span className="text-muted-foreground">{campaign.type} campaign</span>
            </div>
          </div>
        </div>

        {/* Key Metrics */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <Card className="bg-card border-border p-6">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-lg bg-primary/20 flex items-center justify-center">
                <Mail className="w-6 h-6 text-primary" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Emails Sent</p>
                <p className="text-2xl font-bold text-white">{formatNumber(campaign.sent)}</p>
              </div>
            </div>
          </Card>

          <Card className="bg-card border-border p-6">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-lg bg-blue-500/20 flex items-center justify-center">
                <Eye className="w-6 h-6 text-blue-500" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Opens</p>
                <p className="text-2xl font-bold text-white">{formatNumber(campaign.opened)}</p>
                <p className="text-xs text-blue-500">{openRate}% open rate</p>
              </div>
            </div>
          </Card>

          <Card className="bg-card border-border p-6">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-lg bg-accent/20 flex items-center justify-center">
                <MousePointer className="w-6 h-6 text-accent" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Clicks</p>
                <p className="text-2xl font-bold text-white">{formatNumber(campaign.clicked)}</p>
                <p className="text-xs text-accent">{clickRate}% click rate</p>
              </div>
            </div>
          </Card>

          <Card className="bg-card border-border p-6">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-lg bg-green-500/20 flex items-center justify-center">
                <DollarSign className="w-6 h-6 text-green-500" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Revenue</p>
                <p className="text-2xl font-bold text-white">{formatCurrency(campaign.revenue)}</p>
              </div>
            </div>
          </Card>
        </div>

        {/* Funnel Visualization */}
        <Card className="bg-card border-border p-6 mb-8">
          <h2 className="text-xl font-bold text-white mb-6">Campaign Funnel</h2>
          <div className="space-y-4">
            {/* Sent */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <span className="text-white">Sent</span>
                <span className="text-muted-foreground">{formatNumber(campaign.sent)}</span>
              </div>
              <div className="w-full h-8 bg-secondary rounded-lg overflow-hidden">
                <div className="h-full bg-primary" style={{ width: '100%' }} />
              </div>
            </div>

            {/* Opened */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <span className="text-white">Opened</span>
                <span className="text-muted-foreground">{formatNumber(campaign.opened)} ({openRate}%)</span>
              </div>
              <div className="w-full h-8 bg-secondary rounded-lg overflow-hidden">
                <div className="h-full bg-blue-500" style={{ width: `${openRate}%` }} />
              </div>
            </div>

            {/* Clicked */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <span className="text-white">Clicked</span>
                <span className="text-muted-foreground">{formatNumber(campaign.clicked)} ({clickRate}%)</span>
              </div>
              <div className="w-full h-8 bg-secondary rounded-lg overflow-hidden">
                <div className="h-full bg-accent" style={{ width: `${clickRate}%` }} />
              </div>
            </div>

            {/* Conversions */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <span className="text-white">Conversions</span>
                <span className="text-muted-foreground">{formatNumber(campaign.conversions)} ({conversionRate}% of clicks)</span>
              </div>
              <div className="w-full h-8 bg-secondary rounded-lg overflow-hidden">
                <div className="h-full bg-green-500" style={{ width: `${(campaign.conversions / campaign.sent) * 100}%` }} />
              </div>
            </div>
          </div>
        </Card>

        {/* Performance Summary */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <Card className="bg-card border-border p-6">
            <h3 className="text-lg font-semibold text-white mb-4">Performance Summary</h3>
            <div className="space-y-3">
              <div className="flex justify-between">
                <span className="text-muted-foreground">Open Rate</span>
                <span className="text-white font-semibold">{openRate}%</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Click Rate</span>
                <span className="text-white font-semibold">{clickRate}%</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Conversion Rate</span>
                <span className="text-white font-semibold">{conversionRate}%</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Revenue per Email</span>
                <span className="text-primary font-semibold">{formatCurrency(campaign.revenue / campaign.sent)}</span>
              </div>
            </div>
          </Card>

          <Card className="bg-card border-border p-6">
            <h3 className="text-lg font-semibold text-white mb-4">Revenue Breakdown</h3>
            <div className="space-y-3">
              <div className="flex justify-between">
                <span className="text-muted-foreground">Total Revenue</span>
                <span className="text-green-500 font-semibold">{formatCurrency(campaign.revenue)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Conversions</span>
                <span className="text-white font-semibold">{campaign.conversions}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Avg. Order Value</span>
                <span className="text-white font-semibold">{formatCurrency(campaign.revenue / campaign.conversions)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">ROI</span>
                <span className="text-accent font-semibold">+{((campaign.revenue / 100) * 100).toFixed(0)}%</span>
              </div>
            </div>
          </Card>
        </div>
      </div>
    </AdminLayout>
  );
}

export default AdminCampaignMetrics;
